import { useRef } from 'react'
import useMountEffect from './useMountEffect'
import { useAppContext } from '../contexts/AppContext'

export default function useSlideWidthAdjustment() {
  const { rootRef } = useAppContext()
  const parentRef = useRef<HTMLElement>(null)

  useMountEffect(() => {
    if (!rootRef.current) return

    const root = rootRef.current

    // Find parent element: shadow host's parent if inside a shadow DOM,
    // otherwise the direct root's parent
    const shadowHostParent = (root.getRootNode() as ShadowRoot).host?.parentElement
    const parent = shadowHostParent || root.parentElement
    if (!parent) return
    parentRef.current = parent

    // Observe the available viewport based on the parent width,
    // and set the slide width CSS var accordingly
    const updateSlideWidth = () => {
      root.style.setProperty('--slide-width', `${parent.clientWidth}px`)
    }
    const observer = new ResizeObserver(updateSlideWidth)
    observer.observe(parent)

    // Set the initial slide width on mount
    updateSlideWidth()

    return () => {
      observer.disconnect()
    }
  })

  useMountEffect(() => {
    if (!rootRef.current) return

    const onResize = () => {
      const parentClientWidth = parentRef.current?.clientWidth || 0
      rootRef.current!.style.setProperty('--slide-width', `${parentClientWidth}px`)
    }

    addEventListener('resize', onResize)
    return () => {
      removeEventListener('resize', onResize)
    }
  })
}
